import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle, Car, ClipboardList, Image as ImageIcon, MapPin,
  Loader2, MessageSquare, Phone, QrCode, Store, Truck, User, XCircle,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ChefHeader from '../shared/ui/ChefHeader';
import MapView from '../shared/MapView';
import { Card, EmptyState, LoadingView, PrimaryButton, StatusBadge } from '../shared/ui';
import {
  OrderStatus, PLATFORM_COMMISSION_RATE, formatCurrency, formatDate, formatTime,
  restaurantPayout, toLatLng,
} from '../shared/models';
import {
  listenToOrder, markOrderPreparing,
  markOrderReady, restaurantAcceptOrder,
} from '../shared/services/orderService';
import CancelOrderSheet from '../components/orders/CancelOrderSheet';
import RejectOrderSheet from '../components/orders/RejectOrderSheet';

const FINAL_STATUSES = [OrderStatus.DELIVERED, OrderStatus.COMPLETED, OrderStatus.CANCELLED];
const DRIVER_ACTIVE_STATUSES = [
  OrderStatus.PICKED_UP, OrderStatus.ON_MY_WAY, OrderStatus.IN_DELIVERY, OrderStatus.ARRIVED,
];

const cleanPhone = (p) => (p || '').replace(/[^0-9+]/g, '');

function SummaryRow({ label, value, className = '', bold = false }) {
  return (
    <div className="flex justify-between text-sm">
      <span className={bold ? 'font-bold text-gray-900' : 'text-gray-600'}>{label}</span>
      <span className={`font-semibold ${bold ? 'text-gray-900 font-bold' : 'text-gray-900'} ${className}`}>{value}</span>
    </div>
  );
}

// Mirrors RestaurantOrderDetailView.swift — live order listener, status card,
// items + payout summary, delivery info, assigned driver, mini map, and the
// status-driven action buttons (with the NewOrderDetailsView accept/reject
// block for Pending orders).
export default function OrderDetailPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { restaurant } = useAuth();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [busy, setBusy] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  useEffect(() => {
    if (!orderId) return undefined;
    setLoading(true);
    return listenToOrder(orderId,
      (o) => { setOrder(o); setLoading(false); setError(''); },
      (e) => { setError(e.message || 'Failed to load order.'); setLoading(false); });
  }, [orderId]);

  const isDelivery = (order?.orderType || '').toLowerCase() === 'delivery';
  const isFinal = order ? FINAL_STATUSES.includes(order.status) : false;

  const markers = useMemo(() => {
    if (!order) return [];
    const restCoord = toLatLng(order.restaurantLocation?.coordinates || order.restaurantLocation)
      || toLatLng(restaurant?.coordinates);
    const custCoord = toLatLng(order.deliveryAddress?.coordinates || order.deliveryAddress);
    const list = [];
    if (restCoord) list.push({ ...restCoord, kind: 'restaurant', popup: order.restaurantLocation?.name || order.restaurantName || 'Restaurant' });
    if (isDelivery && custCoord) list.push({ ...custCoord, kind: 'customer', popup: order.deliveryAddress?.name || 'Customer' });
    return list;
  }, [order, restaurant, isDelivery]);

  const act = (key, fn, after) => async () => {
    if (busy) return;
    setBusy(key);
    setActionError('');
    try {
      await fn();
      after?.();
    } catch (e) {
      setActionError(e.message || 'Action failed. Please try again.');
    } finally {
      setBusy('');
    }
  };

  const markReady = act('ready', () => markOrderReady(order),
    () => navigate(`/business/orders/${order.id}/receipt`)); // iOS auto-opens the receipt sheet on Ready

  const busyBtn = (key, label) => (
    <span className="inline-flex items-center justify-center gap-2">
      {busy === key ? <Loader2 size={16} className="animate-spin" /> : null}
      {label}
    </span>
  );

  return (
    <div>
      <ChefHeader
        title="Order Details"
        right={(
          <button
            onClick={() => navigate('/business/orders')}
            className="text-sm font-semibold text-[#FF6B35] px-2"
          >
            Done
          </button>
        )}
      />

      {loading ? (
        <LoadingView label="Loading order…" />
      ) : error && !order ? (
        <EmptyState icon={AlertTriangle} title="Couldn't load order" subtitle={error} />
      ) : !order ? (
        <EmptyState icon={ClipboardList} title="Order not found" subtitle="It may have been deleted." />
      ) : (
        <div className="p-3 space-y-3">
          {actionError && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-100 rounded-xl p-3">
              <AlertTriangle size={16} className="text-red-500 shrink-0 mt-0.5" />
              <p className="text-xs font-medium text-red-600">{actionError}</p>
            </div>
          )}

          {/* Order Status card */}
          <Card className="p-4 space-y-3">
            <p className="text-sm font-bold text-gray-900">Order Summary</p>
            <div className="flex items-start justify-between gap-3">
              <StatusBadge status={order.status} />
              <div className="text-right">
                <p className="text-[11px] text-gray-400">Order #{String(order.id).slice(0, 8)}</p>
                <p className="text-[11px] text-gray-400">{formatDate(order.createdAt)}</p>
                <p className="text-[11px] text-gray-400">{formatTime(order.createdAt)}</p>
              </div>
            </div>
            <p className="text-[11px] text-gray-400">
              {order.orderType}{order.paymentMethod ? ` • ${order.paymentMethod}` : ''}
            </p>

            <div className="border-t border-gray-100 pt-3 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <User size={15} className="text-blue-500 shrink-0" />
                  <span className="text-sm font-medium text-gray-900 truncate">
                    {order.customerName || 'Customer'}
                  </span>
                </div>
                {order.customerPhone && (
                  <a
                    href={`tel:${cleanPhone(order.customerPhone)}`}
                    className="flex items-center gap-1.5 bg-blue-500 text-white text-xs font-semibold px-3 py-1.5 rounded-lg shrink-0 active:opacity-80"
                  >
                    <Phone size={13} />
                    {order.customerPhone}
                  </a>
                )}
              </div>

              {isDelivery && (
                <div className="pt-1">
                  <p className="text-xs font-semibold text-gray-500 mb-1.5">Delivery Address</p>
                  {order.deliveryAddress ? (
                    <div className="space-y-0.5">
                      {order.deliveryAddress.name && (
                        <p className="text-sm font-semibold text-gray-900">{order.deliveryAddress.name}</p>
                      )}
                      {order.deliveryAddress.street && (
                        <p className="text-xs text-gray-500">{order.deliveryAddress.street}</p>
                      )}
                      {order.deliveryAddress.apartment && (
                        <p className="text-xs text-gray-500">Apt: {order.deliveryAddress.apartment}</p>
                      )}
                      {order.deliveryAddress.parish && (
                        <p className="text-xs text-gray-500">{order.deliveryAddress.parish}</p>
                      )}
                      {order.deliveryAddress.island && (
                        <p className="text-xs text-gray-500">{order.deliveryAddress.island}</p>
                      )}
                      {order.deliveryAddress.landmark && (
                        <p className="text-xs text-gray-500">Near: {order.deliveryAddress.landmark}</p>
                      )}
                      {order.deliveryAddress.instructions && (
                        <p className="text-xs text-orange-500 italic">{order.deliveryAddress.instructions}</p>
                      )}
                    </div>
                  ) : order.customerAddress ? (
                    <p className="text-sm text-gray-700">{order.customerAddress}</p>
                  ) : (
                    <p className="text-xs text-gray-400">Address not available</p>
                  )}
                </div>
              )}

              {!isDelivery && (
                <p className="text-xs text-gray-400">
                  Pickup — the customer will collect this order at your restaurant.
                </p>
              )}

              {order.specialInstructions && (
                <div className="flex items-start gap-2 pt-1">
                  <MessageSquare size={15} className="text-orange-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-gray-500">{order.specialInstructions}</p>
                </div>
              )}
            </div>
          </Card>

          {/* Order Items + payout summary */}
          <Card className="p-4">
            <p className="text-sm font-bold text-gray-900 mb-2">Order Items</p>
            <div className="divide-y divide-gray-50">
              {order.orderItems.map((item, idx) => (
                <div key={item.id || idx} className="flex gap-3 py-2.5">
                  <div className="relative w-[60px] h-[60px] rounded-lg bg-gray-100 overflow-hidden shrink-0 flex items-center justify-center text-gray-300">
                    <ImageIcon size={22} />
                    {item.imageURL && (
                      <img
                        src={item.imageURL}
                        alt={item.menuItemName}
                        className="absolute inset-0 w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{item.menuItemName}{item.variantName ? ` (Size: ${item.variantName})` : ''}</p>
                    <p className="text-xs text-gray-400 mt-0.5">Quantity: {item.quantity}</p>
                    {item.specialInstructions && (
                      <p className="text-[11px] text-orange-600 bg-orange-50 rounded px-1.5 py-0.5 mt-1 inline-block">
                        Note: {item.specialInstructions}
                      </p>
                    )}
                  </div>
                  <p className="text-sm font-semibold text-green-600 shrink-0">{formatCurrency(item.totalPrice)}</p>
                </div>
              ))}
            </div>

            <div className="border-t border-gray-100 mt-2 pt-3 space-y-2">
              <SummaryRow label="Subtotal" value={formatCurrency(order.subtotal)} />
              <div className="border-t border-gray-100 pt-2 space-y-2">
                <SummaryRow
                  label="Platform Commission (10%)"
                  value={formatCurrency(order.subtotal * PLATFORM_COMMISSION_RATE)}
                  className="text-orange-500"
                />
                <SummaryRow
                  label="Your Pay (90%)"
                  value={formatCurrency(restaurantPayout(order))}
                  className="text-green-600"
                />
              </div>
            </div>

            {/* Restaurant name + real address (matches iOS merged card) */}
            <div className="border-t border-gray-100 mt-3 pt-3 space-y-1.5">
              <div className="flex items-center gap-2">
                <Store size={15} className="text-blue-500 shrink-0" />
                <span className="text-sm font-medium text-gray-900">
                  {order.restaurantLocation?.name || order.restaurantName || restaurant?.name || 'Restaurant'}
                </span>
              </div>
              {(() => {
                const addr = order.restaurantLocation?.address || restaurant?.address || '';
                if (!addr || addr === 'Restaurant Address' || addr === 'Address pending') return null;
                return (
                  <div className="flex items-start gap-2">
                    <MapPin size={15} className="text-red-500 shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-500">{addr}</span>
                  </div>
                );
              })()}
            </div>
          </Card>



          {/* Assigned Driver */}
          {order.driverId && (
            <Card className="p-4 space-y-2">
              <p className="text-sm font-bold text-gray-900">Assigned Driver</p>
              <div className="flex items-center gap-2">
                <Car size={15} className="text-green-600" />
                <span className="text-sm text-gray-800">{order.driverName || 'Driver'}</span>
              </div>
            </Card>
          )}

          {/* Locations map (delivery orders, mirrors the 200pt MapKit view) */}
          {isDelivery && markers.length > 0 && (
            <Card className="p-4">
              <p className="text-sm font-bold text-gray-900 mb-2">Locations</p>
              <div className="h-[200px] rounded-xl overflow-hidden shadow-sm">
                <MapView markers={markers} />
              </div>
            </Card>
          )}

          {/* Cancellation info */}
          {order.status === OrderStatus.CANCELLED && (order.cancellationReason || order.cancelledBy) && (
            <Card className="p-4 bg-red-50 border-red-100">
              <p className="text-sm font-bold text-red-600">Order Cancelled</p>
              {order.cancellationReason && (
                <p className="text-xs text-red-500 mt-1">Reason: {order.cancellationReason}</p>
              )}
              {order.cancelledBy && (
                <p className="text-[11px] text-red-400 mt-0.5">Cancelled by: {order.cancelledBy}</p>
              )}
            </Card>
          )}

          {/* Action buttons — driven by status like RestaurantOrderDetailViewModel */}
          <div className="space-y-2.5 pt-1 pb-2">
            {order.status === OrderStatus.PENDING && (
              <>
                <PrimaryButton
                  color="bg-green-500"
                  disabled={!!busy}
                  onClick={act('accept', () => restaurantAcceptOrder(order))}
                >
                  {busyBtn('accept', 'Accept Order')}
                </PrimaryButton>
                <PrimaryButton
                  color="bg-red-500"
                  disabled={!!busy}
                  onClick={() => setRejectOpen(true)}
                >
                  <span className="inline-flex items-center justify-center gap-2">
                    <XCircle size={16} /> Reject Order
                  </span>
                </PrimaryButton>
                <p className="text-[11px] text-gray-400 text-center px-4">
                  By accepting this order, you confirm that you can prepare and fulfill it
                  according to the customer's requirements.
                </p>
              </>
            )}

            {order.status === OrderStatus.CONFIRMED && (
              <PrimaryButton
                color="bg-blue-500"
                disabled={!!busy}
                onClick={act('prepare', () => markOrderPreparing(order))}
              >
                {busyBtn('prepare', 'Mark Preparing')}
              </PrimaryButton>
            )}

            {order.status === OrderStatus.PREPARING && (
              <PrimaryButton color="bg-green-500" disabled={!!busy} onClick={markReady}>
                {busyBtn('ready', 'Mark Ready')}
              </PrimaryButton>
            )}

            {order.status === OrderStatus.READY && (
              <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-xl p-3">
                {isDelivery ? (
                  <Truck size={16} className="text-blue-500 shrink-0 mt-0.5" />
                ) : (
                  <User size={16} className="text-blue-500 shrink-0 mt-0.5" />
                )}
                <p className="text-xs font-medium text-blue-600">
                  {isDelivery
                    ? 'Order is ready — awaiting driver pickup. Show the receipt QR code to the driver.'
                    : 'Order is ready — awaiting customer pickup. Show the receipt QR code to the customer.'}
                </p>
              </div>
            )}

            {DRIVER_ACTIVE_STATUSES.includes(order.status) && (
              <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-xl p-3">
                <Truck size={16} className="text-blue-500 shrink-0 mt-0.5" />
                <p className="text-xs font-medium text-blue-600">
                  This order is with the driver ({order.status}).
                </p>
              </div>
            )}

            {order.status !== OrderStatus.CANCELLED && (
              <Link
                to={`/business/orders/${order.id}/receipt`}
                className="w-full py-3 rounded-2xl bg-white border border-gray-200 text-gray-800 font-bold text-sm shadow-sm flex items-center justify-center gap-2 active:bg-gray-50"
              >
                <QrCode size={16} className="text-[#FF6B35]" />
                View Receipt & QR Code
              </Link>
            )}

            {!isFinal && order.status !== OrderStatus.PENDING && (
              <button
                onClick={() => setCancelOpen(true)}
                disabled={!!busy}
                className="w-full py-3 rounded-2xl border border-red-200 text-red-500 font-bold text-sm active:bg-red-50 disabled:opacity-50"
              >
                Cancel Order
              </button>
            )}
          </div>
        </div>
      )}

      {order && (
        <>
          <RejectOrderSheet order={order} open={rejectOpen} onClose={() => setRejectOpen(false)} />
          <CancelOrderSheet order={order} open={cancelOpen} onClose={() => setCancelOpen(false)} />
        </>
      )}
    </div>
  );
}
